/**
 * Browser Mode Shell
 *
 * Wraps the app in a fake macOS window frame with:
 * - Desktop-like gradient background
 * - Fake macOS menu bar with emoji logo and real time
 * - Draggable and resizable window
 * - Dialog containment within the app window
 */

import { ReactNode, useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { FakeMenuBar } from './FakeMenuBar';
import { BrowserModePortalProvider } from '../browserModePortalContext';
import { PRODUCT_NAME } from '../browserModeUtils';

type BrowserModeShellProps = {
  children: ReactNode;
};

type WindowPosition = {
  x: number;
  y: number;
};

type WindowSize = {
  width: number;
  height: number;
};

const MIN_WIDTH = 600;
const MIN_HEIGHT = 400;

/*
 * The window fills the desktop minus these insets, capped at MAX_*. The bottom
 * inset is deliberately larger than the top one so the desktop's Download CTA
 * (bottom-right, outside the window) never overlaps the app content.
 */
const MAX_WIDTH = 1920;
const MAX_HEIGHT = 1200;
const INSET_X = 48;
const INSET_TOP = 32;
const INSET_BOTTOM = 96;

/** Height of the fake menu bar above the workspace (see .fake-menubar). */
const MENUBAR_HEIGHT = 25;

/** Largest window that fits the desktop, centred horizontally, biased to the top. */
const fitWindow = (workspaceWidth: number, workspaceHeight: number) => {
  const width = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, workspaceWidth - INSET_X * 2));
  const height = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, workspaceHeight - INSET_TOP - INSET_BOTTOM));

  return {
    size: { width, height },
    position: {
      x: Math.max(0, (workspaceWidth - width) / 2),
      y: Math.max(0, Math.min(INSET_TOP, workspaceHeight - height)),
    },
  };
};

export function BrowserModeShell({ children }: BrowserModeShellProps) {
  const workspaceRef = useRef<HTMLDivElement>(null);
  const windowRef = useRef<HTMLDivElement>(null);
  const portalContainerRef = useRef<HTMLDivElement>(null);

  // Window position and size state, seeded from the viewport so the first paint
  // is already the right size (the effect below refines it from the real rect).
  const initialFit = useMemo(() => fitWindow(window.innerWidth, window.innerHeight - MENUBAR_HEIGHT), []);
  const [position, setPosition] = useState<WindowPosition>(initialFit.position);
  const [size, setSize] = useState<WindowSize>(initialFit.size);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeEdge, setResizeEdge] = useState<string | null>(null);
  const dragStart = useRef<{ x: number; y: number; posX: number; posY: number }>({ x: 0, y: 0, posX: 0, posY: 0 });
  const resizeStart = useRef<{ x: number; y: number; width: number; height: number; posX: number; posY: number }>({
    x: 0, y: 0, width: 0, height: 0, posX: 0, posY: 0
  });

  // Size the window to the desktop on initial render
  useEffect(() => {
    if (workspaceRef.current) {
      const rect = workspaceRef.current.getBoundingClientRect();
      const fit = fitWindow(rect.width, rect.height);

      setSize(fit.size);
      setPosition(fit.position);
    }
  }, []);

  // Handle window resize to keep window in bounds
  useEffect(() => {
    const handleResize = () => {
      if (!workspaceRef.current || !windowRef.current) return;
      const workspace = workspaceRef.current.getBoundingClientRect();

      setPosition((prev) => ({
        x: Math.min(prev.x, Math.max(0, workspace.width - size.width)),
        y: Math.min(prev.y, Math.max(0, workspace.height - size.height)),
      }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [size]);

  // Drag handlers
  const handleTitleBarMouseDown = useCallback((e: React.MouseEvent) => {
    // Only start drag on left click and not on traffic lights
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest('.browser-mode-traffic-lights')) return;

    setIsDragging(true);
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      posX: position.x,
      posY: position.y,
    };
    e.preventDefault();
  }, [position]);

  // Resize handlers
  const handleResizeMouseDown = useCallback((edge: string, e: React.MouseEvent) => {
    if (e.button !== 0) return;
    setIsResizing(true);
    setResizeEdge(edge);
    resizeStart.current = {
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height,
      posX: position.x,
      posY: position.y,
    };
    e.preventDefault();
    e.stopPropagation();
  }, [size, position]);

  // Global mouse move/up handlers
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!workspaceRef.current) return;
      const workspace = workspaceRef.current.getBoundingClientRect();

      if (isDragging) {
        const deltaX = e.clientX - dragStart.current.x;
        const deltaY = e.clientY - dragStart.current.y;

        let newX = dragStart.current.posX + deltaX;
        let newY = dragStart.current.posY + deltaY;

        // Keep window in bounds
        newX = Math.max(0, Math.min(newX, workspace.width - size.width));
        newY = Math.max(0, Math.min(newY, workspace.height - size.height));

        setPosition({ x: newX, y: newY });
      }

      if (isResizing && resizeEdge) {
        const deltaX = e.clientX - resizeStart.current.x;
        const deltaY = e.clientY - resizeStart.current.y;

        let newWidth = resizeStart.current.width;
        let newHeight = resizeStart.current.height;
        let newX = resizeStart.current.posX;
        let newY = resizeStart.current.posY;

        // Handle resize based on edge
        if (resizeEdge.includes('e')) {
          newWidth = Math.max(MIN_WIDTH, resizeStart.current.width + deltaX);
        }
        if (resizeEdge.includes('w')) {
          const widthDelta = resizeStart.current.width - deltaX;
          if (widthDelta >= MIN_WIDTH) {
            newWidth = widthDelta;
            newX = resizeStart.current.posX + deltaX;
          }
        }
        if (resizeEdge.includes('s')) {
          newHeight = Math.max(MIN_HEIGHT, resizeStart.current.height + deltaY);
        }
        if (resizeEdge.includes('n')) {
          const heightDelta = resizeStart.current.height - deltaY;
          if (heightDelta >= MIN_HEIGHT) {
            newHeight = heightDelta;
            newY = resizeStart.current.posY + deltaY;
          }
        }

        // Keep in bounds
        const maxWidth = workspace.width - newX;
        const maxHeight = workspace.height - newY;
        newWidth = Math.min(newWidth, maxWidth);
        newHeight = Math.min(newHeight, maxHeight);
        newX = Math.max(0, newX);
        newY = Math.max(0, newY);

        setSize({ width: newWidth, height: newHeight });
        setPosition({ x: newX, y: newY });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      setResizeEdge(null);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, resizeEdge, size]);

  return (
    <div className="browser-mode-desktop">
      {/* Fake macOS menu bar */}
      <FakeMenuBar />

      {/* Desktop workspace area */}
      <div className="browser-mode-workspace" ref={workspaceRef}>
        {/* Desktop background */}
        <div className="browser-mode-background" />

        {/* Download button on desktop - bottom right */}
        <a
          href="https://jamsql.com/?utm_source=app&utm_medium=referral&utm_campaign=demo-shell#download"
          className="browser-mode-download-btn"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Download
        </a>

        {/* macOS-style window */}
        <div
          ref={windowRef}
          className="browser-mode-window"
          style={{
            left: position.x,
            top: position.y,
            width: size.width,
            height: size.height,
            cursor: isDragging ? 'grabbing' : undefined,
          }}
        >
          {/* Resize handles */}
          <div
            className="browser-mode-resize-handle browser-mode-resize-n"
            onMouseDown={(e) => handleResizeMouseDown('n', e)}
          />
          <div
            className="browser-mode-resize-handle browser-mode-resize-s"
            onMouseDown={(e) => handleResizeMouseDown('s', e)}
          />
          <div
            className="browser-mode-resize-handle browser-mode-resize-e"
            onMouseDown={(e) => handleResizeMouseDown('e', e)}
          />
          <div
            className="browser-mode-resize-handle browser-mode-resize-w"
            onMouseDown={(e) => handleResizeMouseDown('w', e)}
          />
          <div
            className="browser-mode-resize-handle browser-mode-resize-ne"
            onMouseDown={(e) => handleResizeMouseDown('ne', e)}
          />
          <div
            className="browser-mode-resize-handle browser-mode-resize-nw"
            onMouseDown={(e) => handleResizeMouseDown('nw', e)}
          />
          <div
            className="browser-mode-resize-handle browser-mode-resize-se"
            onMouseDown={(e) => handleResizeMouseDown('se', e)}
          />
          <div
            className="browser-mode-resize-handle browser-mode-resize-sw"
            onMouseDown={(e) => handleResizeMouseDown('sw', e)}
          />

          {/* Inner container to clip content */}
          <div className="browser-mode-window-inner">
            {/* Title bar */}
            <div
              className="browser-mode-titlebar"
              onMouseDown={handleTitleBarMouseDown}
              style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
            >
              {/* Traffic lights */}
              <div className="browser-mode-traffic-lights">
                <div className="browser-mode-traffic-light browser-mode-traffic-light-close" />
                <div className="browser-mode-traffic-light browser-mode-traffic-light-minimize" />
                <div className="browser-mode-traffic-light browser-mode-traffic-light-maximize" />
              </div>

              {/* Window title */}
              <div className="browser-mode-window-title">
                {PRODUCT_NAME}
              </div>

              {/* Spacer to center the title */}
              <div className="browser-mode-traffic-lights-spacer" />
            </div>

            {/* Window content */}
            <div className="browser-mode-content">
              <BrowserModePortalProvider value={{ portalContainer: portalContainerRef }}>
                {children}
              </BrowserModePortalProvider>

              {/* Portal container for dialogs - inside content area only */}
              <div
                ref={portalContainerRef}
                className="browser-mode-portal-container"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
